import { user } from "@/services/user";
import { useMutation } from "react-query";
import { AxiosError } from "axios";
import { setMyCookie } from "@/helper/helper";
import { IUserBasicLogin, LoginProps } from "@/types/user.types";
import { useError } from "@/states/common.state";
import { useUserState } from "@/states/user.state";
import { useQrShowState } from "@/states/qr.state";
import { useRouter } from "next/router";
import { useSolutionsSelect } from "@/states/solution.state";

type UseLoginOptions = {
  locale?: string;
  viaSignature?: boolean;
  setAlertError?: (error: string) => void;
};

const useLogin = ({
  setAlertError,
  viaSignature = false,
  locale = "en",
}: UseLoginOptions = {}): LoginProps => {
  const userState = useUserState.getState();
  const { dashboardQr, setDownloadQrId, destroyDashboardQr } = useQrShowState.getState();
  const { setSolutionsType, solutionsSelectedTypes } = useSolutionsSelect.getState()
  const router = useRouter();

  const mutation = useMutation(
    (params: Record<string, any>) => handleLoginRequest(params, viaSignature, dashboardQr),
    {
      onSuccess: async (response) => {

        const qrType = response.qrType;

        setSolutionsType({ solutionSelectedType: qrType ?? solutionsSelectedTypes})

        handleSuccess(response, viaSignature, userState, setDownloadQrId, router, locale, destroyDashboardQr)
      },
      onError: (error) => handleError(error, setAlertError),
    },
  );

  return {
    setMutateLoginAsync: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    error: mutation.error as AxiosError,
  };
};

// Handle login request
const handleLoginRequest = (
  params: Record<string, any>,
  viaSignature: boolean | undefined,
  dashboardQr: Record<string, any> | null,
) => {
  return user.postLogin({ ...params, qrcode: { ...dashboardQr }, via_signature: viaSignature });
};

// Handle successful login
const handleSuccess = async (
  { data, redirect, qrcode, ...extraData }: any,
  viaSignature: boolean | undefined,
  userState: any,
  setDownloadQrId: (val: number | null) => void,
  router: any,
  locale: string,
  destroyDashboardQr: () => void,
) => {
  if (viaSignature) {
    destroyDashboardQr();
    redirectToSignature(extraData, userState, redirect, qrcode, locale);
    return;
  }

  processLoginData(data, userState);
  handleRedirect(redirect, qrcode, setDownloadQrId, router);
};

// Redirect for signature-based login
const redirectToSignature = (
  extraData: Record<string, any>,
  userState: any,
  redirect: string,
  qrcode: string | null,
  locale: string,
) => {
  const redirectUrl = new URL(`${process.env.NEXT_PUBLIC_SITE_MAIN_URL}signature-auth`);

  appendSearchParams(redirectUrl, extraData);
  if (userState.guestToken) {
    redirectUrl.searchParams.append("guestToken", userState.guestToken);
  }
  redirectUrl.searchParams.append("redirect", redirect || "");
  redirectUrl.searchParams.append("qrcode", qrcode || "");
  redirectUrl.searchParams.append("locale", locale || "en");

  window.location.href = redirectUrl.toString();
};

// Append search parameters to the URL
const appendSearchParams = (url: URL, params: Record<string, any>) => {
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.append(key, String(value));
  });
};

// Process user data on successful login
const processLoginData = (data: IUserBasicLogin, userState: any) => {
const { setSolutionsType } = useSolutionsSelect.getState();




  userState.setUser(data);
  setMyCookie("access_token", data.tokens.access_token, 30);

  if (data.ab_versions) {
    Object.entries(data.ab_versions).forEach(([key, version]) => {
      userState.setVersionInfo(key, version);
    });
  }
};

// Handle redirect after successful login
const handleRedirect = (
  redirect: string | null,
  qrcode: string | null,
  setDownloadQrId: any,
  router: any,
) => {
  if (redirect) {
    setDownloadQrId(qrcode || null);
    router.push(redirect, undefined, { locale: false });
  }
};

// Handle error during login
const handleError = (error: any, setAlertError?: (error: string) => void) => {
  const errors = error.response?.data?.errors;
  const { setError } = useError.getState();

  if (errors?.emailAlertError) {
    setAlertError?.(errors.emailAlertError);
  } else {
    setError(errors);
  }
};

export default useLogin;
